import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import {
  LayoutDashboard,
  Image as ImageIcon,
  Newspaper,
  Calendar,
  BookOpen,
  Church,
  Heart,
  Users,
  LogOut,
  Shield,
  PanelTop,
  Megaphone,
  Library,
  GraduationCap,
  Home,
  Package,
  Cross,
  Palette,
  ToggleLeft,
  Camera,
} from 'lucide-react';
import { toast } from 'sonner';

// Import all manager components
import DashboardView from './components/DashboardView';
import HeroesManager from './components/HeroesManager';
import PageBannersManager from './components/PageBannersManager';
import NewsManager from './components/NewsManager';
import EventsManager from './components/EventsManager';
import PhotosManager from './components/PhotosManager';
import BibleReadingsManager from './components/BibleReadingsManager';
import PrayersManager from './components/PrayersManager';
import MassSchedulesManager from './components/MassSchedulesManager';
import LiturgiManager from './components/LiturgiManager';
import LanguageSettingsManager from './components/LanguageSettingsManager';
import UsersManager from './components/UsersManager';
import AnnouncementsManager from './components/AnnouncementsManager';
import ArticlesManager from './components/ArticlesManager';
import ParishPagesManager from './components/ParishPagesManager';
import PriestBlogsManager from './components/PriestBlogsManager';
import CatalogueManager from './components/CatalogueManager';
import GlossaryManager from './components/GlossaryManager';
import MediaLibrary from './components/MediaLibrary';
import PhotoAlbumsManager from './components/PhotoAlbumsManager';
import FeaturedAnnouncementsManager from './components/FeaturedAnnouncementsManager';
import ThemeManager from './components/ThemeManager';
import ModulesManager from './components/ModulesManager';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, logout, user, canAccessModule } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Laddar...</div>;
  }

  if (!isAuthenticated) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Utloggad');
  };

  // Define all tabs with permission requirements
  const allTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard' },
    { id: 'heroes', label: 'Heroes', icon: ImageIcon, module: 'heroes' },
    { id: 'banners', label: 'Sidbannrar', icon: PanelTop, module: 'banners' },
    { id: 'media', label: 'Bildbibliotek', icon: ImageIcon, module: 'heroes' },
    { id: 'albums', label: 'Fotoalbum', icon: Camera, module: 'heroes' },
    { id: 'featured', label: 'Utvalda', icon: Megaphone, module: 'news' },
    { id: 'announcements', label: 'Meddelanden', icon: Megaphone, module: 'news' },
    { id: 'news', label: 'Nyheter', icon: Newspaper, module: 'news' },
    { id: 'articles', label: 'Fördjupning', icon: Library, module: 'news' },
    { id: 'priest-blogs', label: 'Prästens hörna', icon: Heart, module: 'news' },
    { id: 'parish-pages', label: 'Församling', icon: GraduationCap, module: 'news' },
    { id: 'catalogue', label: 'Katalog', icon: Package, module: 'news' },
    { id: 'liturgi', label: 'Liturgi', icon: Cross, module: 'events' },
    { id: 'mass-schedules', label: 'Mässcheman', icon: Church, module: 'mass_schedules' },
    { id: 'bible', label: 'Bibelläsningar', icon: BookOpen, module: 'bible' },
    { id: 'prayers', label: 'Böner', icon: Heart, module: 'prayers' },
    { id: 'glossary', label: 'Ordlista', icon: BookOpen, module: 'languages' },
    { id: 'languages', label: 'Språk', icon: Users, module: 'languages' },
    { id: 'theme', label: 'Tema', icon: Palette, module: 'languages' },
    { id: 'modules', label: 'Moduler', icon: ToggleLeft, module: 'languages' },
    { id: 'users', label: 'Användare', icon: Shield, module: 'users' },
  ];

  // Filter tabs based on user permissions
  const tabs = allTabs.filter(tab => canAccessModule(tab.module));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-gray-900 text-white flex flex-col">
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center space-x-3 mb-4">
            <img 
              src="/favicon-large.png" 
              alt="Vår Fru av Fatima" 
              className="w-8 h-8 rounded-full"
            />
            <h1 className="text-xl font-bold">CMS Admin</h1>
          </div>
          
          {/* Back to Website Button */}
          <Link
            to="/"
            className="flex items-center space-x-3 px-4 py-3 mb-4 rounded-lg bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 hover:text-yellow-300 transition-colors border border-yellow-500/30"
            data-testid="back-to-website-cms"
          >
            <Home className="w-5 h-5" />
            <span>Tillbaka till webbplats</span>
          </Link>

          {/* User Info */}
          {user && (
            <div className="mb-4 p-3 bg-gray-800 rounded-lg">
              <p className="text-sm font-medium text-white truncate">
                {user.display_name || user.username}
              </p>
              <p className="text-xs text-gray-400 capitalize">
                {user.role === 'super_admin' ? 'Super Admin' : 
                 user.role === 'church_admin' ? 'Church Admin' : 'Editor'}
              </p>
            </div>
          )}
        </div>
        
        {/* Scrollable nav area */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <nav className="space-y-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-yellow-500 text-gray-900'
                      : 'hover:bg-gray-800 text-gray-300'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        
        {/* Logout button at bottom */}
        <div className="p-6 flex-shrink-0 border-t border-gray-800">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-800"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logga ut
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        {activeTab === 'dashboard' && canAccessModule('dashboard') && <DashboardView />}
        {activeTab === 'heroes' && canAccessModule('heroes') && <HeroesManager />}
        {activeTab === 'banners' && canAccessModule('banners') && <PageBannersManager />}
        {activeTab === 'media' && canAccessModule('heroes') && <MediaLibrary />}
        {activeTab === 'albums' && canAccessModule('heroes') && <PhotoAlbumsManager />}
        {activeTab === 'featured' && canAccessModule('news') && <FeaturedAnnouncementsManager />}
        {activeTab === 'announcements' && canAccessModule('news') && <AnnouncementsManager />}
        {activeTab === 'news' && canAccessModule('news') && <NewsManager />}
        {activeTab === 'articles' && canAccessModule('news') && <ArticlesManager />}
        {activeTab === 'priest-blogs' && canAccessModule('news') && <PriestBlogsManager />}
        {activeTab === 'parish-pages' && canAccessModule('news') && <ParishPagesManager />}
        {activeTab === 'catalogue' && canAccessModule('news') && <CatalogueManager />}
        {activeTab === 'liturgi' && canAccessModule('events') && <LiturgiManager />}
        {activeTab === 'mass-schedules' && canAccessModule('mass_schedules') && <MassSchedulesManager />}
        {activeTab === 'events' && canAccessModule('events') && <EventsManager />}
        {activeTab === 'bible' && canAccessModule('bible') && <BibleReadingsManager />}
        {activeTab === 'prayers' && canAccessModule('prayers') && <PrayersManager />}
        {activeTab === 'glossary' && canAccessModule('languages') && <GlossaryManager />}
        {activeTab === 'languages' && canAccessModule('languages') && <LanguageSettingsManager />}
        {activeTab === 'theme' && canAccessModule('languages') && <ThemeManager />}
        {activeTab === 'modules' && canAccessModule('languages') && <ModulesManager />}
        {activeTab === 'users' && canAccessModule('users') && <UsersManager />}
      </div>
    </div>
  );
};

export default AdminDashboard;
